import { useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { DesktopHeaderBar } from "../components/layout/AppShell";
import { Button } from "../components/ui/Button";
import { SyncBadge } from "../components/ui/SyncBadge";

export function More() {
  const dmName = useAppStore((s) => s.demo.dmName);
  const activeSiteId = useAppStore((s) => s.demo.activeSiteId);
  const sites = useAppStore((s) => s.sites);
  const isOnline = useAppStore((s) => s.demo.isOnline);
  const toggleOnline = useAppStore((s) => s.toggleOnline);
  const resetDemo = useAppStore((s) => s.resetDemo);
  const injectSupportRequest = useAppStore((s) => s.injectSupportRequest);
  const units = useAppStore((s) => s.units.filter((u) => u.siteId === activeSiteId && u.tenantId));
  const [confirmReset, setConfirmReset] = useState(false);
  const [injectedUnitId, setInjectedUnitId] = useState<string | null>(null);
  const site = sites.find((s) => s.id === activeSiteId);

  return (
    <div className="flex flex-1 flex-col gap-6 p-4">
      <DesktopHeaderBar />
      <h1 className="font-display text-[24px] font-black tracking-tight text-brand-navy">More</h1>

      <section className="rounded-md border border-border bg-card p-4">
        <h2 className="font-action text-[11px] font-bold uppercase tracking-wide text-muted-foreground">District manager</h2>
        <p className="mt-1.5 font-action text-[16px] font-bold text-foreground">{dmName}</p>
        <p className="text-[13px] text-muted-foreground">Active site: {site?.name}, {site?.state}</p>
        <div className="mt-2">
          <SyncBadge />
        </div>
      </section>

      <section className="rounded-md border border-attention/40 bg-attention/10 p-4">
        <h2 className="font-action text-[11px] font-bold uppercase tracking-wide text-attention">Reviewer / demo controls</h2>
        <p className="mt-1 text-[12.5px] text-foreground">
          These controls exist to demonstrate FF01 behavior for review. They are not part of the FieldFlow product.
        </p>

        <div className="mt-3 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 rounded-sm border border-border bg-card px-3.5 py-3">
            <div>
              <p className="font-action text-[14px] font-bold text-foreground">Network condition</p>
              <p className="text-[12px] text-muted-foreground">Toggle offline to see capture continue without a connection.</p>
            </div>
            <Button variant={isOnline ? "quiet" : "primary"} onClick={toggleOnline}>
              {isOnline ? "Go offline" : "Go online"}
            </Button>
          </div>


          <div className="rounded-sm border border-border bg-card px-3.5 py-3">
            <p className="font-action text-[14px] font-bold text-foreground">Inject MyRise support request</p>
            <p className="text-[12px] text-muted-foreground">Simulates a tenant submitting a request from MyRise for an occupied unit at this site.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {units.map((u) => (
                <Button
                  key={u.id}
                  variant="quiet"
                  onClick={() => {
                    injectSupportRequest(u.id);
                    setInjectedUnitId(u.id);
                  }}
                >
                  Unit {u.unitNumber}
                </Button>
              ))}
            </div>
            {injectedUnitId && <p className="mt-2 text-[12px] text-success">Support request created — check Inbox.</p>}
          </div>

          <div className="rounded-sm border border-danger/40 bg-danger/5 px-3.5 py-3">
            <p className="font-action text-[14px] font-bold text-danger">Reset demo</p>
            <p className="text-[12px] text-muted-foreground">Restores all fixture data and clears every action taken in this browser.</p>
            {confirmReset ? (
              <div className="mt-2 flex gap-2">
                <Button
                  variant="quiet"
                  className="border-danger text-danger"
                  onClick={() => {
                    resetDemo();
                    setConfirmReset(false);
                  }}
                >
                  Confirm reset
                </Button>
                <Button variant="ghost" onClick={() => setConfirmReset(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="quiet" className="mt-2 border-danger text-danger" onClick={() => setConfirmReset(true)}>
                Reset demo
              </Button>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-md border border-border bg-card p-4">
        <h2 className="font-action text-[11px] font-bold uppercase tracking-wide text-muted-foreground">About this prototype</h2>
        <p className="mt-1.5 text-[13px] leading-5 text-muted-foreground">
          FF01 — FieldFlow interactive north-star prototype. Runs entirely in this browser; all data is fictional and
          persists only in this device's local storage. No production backend, Jarvis, HubSpot, or MyRise service is
          called. See <code className="font-mono text-[11.5px]">docs/ff01/</code> in the repository for the full
          product model and workflow coverage.
        </p>
      </section>
    </div>
  );
}
